import { Header } from '@bayut-sa/header';
import TenantComponents from '@components';
import tenantConstants from '@constants';
import tenantRoutes from '@routes';
import tenantUtils from '@utils';
import { Tooltip } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import LiteNoDataModalUi from '../../../../components/common/lite-no-data-modal/lite-no-data-modal-ui';
import {
  BayutAppStoreIcon,
  IconArabicBold,
  IconArabicfont,
  PlayStoreBadgeIcon,
} from '../../../../components/utilities/icons';
import { BayutAppStoreArabicIcon, BayutPlayStoreArabicIcon } from '../../../../components/svg';
import { isAndroid } from '../../../../utility/general';
import { useAppAuthentication, useRouteNavigate } from '../../../../hooks';
import { BayutKsaHeaderWrapper, FAB } from './styled';
import { getLink, setCookieUnits } from './utils';
import SavedSearchesModal from './saved-searches-modal';
import {
  useDeleteSavedSearchMutation,
  useLazyGetFavouritePropertiesQuery,
  useLazyGetUsersSavedSearchesQuery,
  useLazyGetUserBookingsQuery,
  useSaveSettingsMutation,
} from '../../../../apis/user';
import { addPropertyEvent } from '../../../../services/analyticsService';
import { getCurrentLocationPath, getLoginPath } from '../../../../utility/utility';
import { getRealmRolesFromIdToken } from '../../../../utility/user';
import { readUserHint } from '../../../../utility/userHint';
import { getClassifiedBaseURL } from '../../../../utility/env';
import { useLocation } from 'react-router-dom';

export const NavBar = ({
  fullWidth = false,
  hideLoginButton = false,
  hideSavedSearches = false,
  hideFavouriteProperties = false,
  hideFloatingPostAdButton = false,
  hideMobileNavBarDrawer = false,
  hideMyListings = false,
  hidePostAd = false,
  hideCurrencyConverter = false,
}) => {
  const { t } = useTranslation();
  const { isMobile, rtl, locale } = useSelector((state) => state.app.AppConfig);
  const userData = useSelector((state) => state?.app?.loginUser?.user);
  const [getUsersSavedSearches] = useLazyGetUsersSavedSearchesQuery();
  const [deleteSavedSearch] = useDeleteSavedSearchMutation();
  const [getFavouriteProperties] = useLazyGetFavouritePropertiesQuery();
  const [saveSetting] = useSaveSettingsMutation();
  const [getBookings] = useLazyGetUserBookingsQuery();
  const [myBookingsCount, setMyBookingsCount] = useState(0);
  const [newLocale, setNewLocale] = useState(locale);
  const [showLocaleModal, setShowLocaleModal] = useState(false);
  const [showChangeCurrencyModal, setShowChangeCurrencyModal] = useState(false);
  const [showChangeAreaUnitModal, setShowChangeAreaUnitModal] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [favouriteProperties, setFavouriteProperties] = useState([]);
  const [isFavpropLoading, setIsFavpropLoading] = useState(false);

  const [noSaveSearchModal, setNoSaveSearchModal] = useState(false);
  const [noFavPropModal, setNoFavPropModal] = useState(false);
  const { onLogout, auth } = useAppAuthentication();
  // Drives the Profolio / TruValue / Host Dashboard / Investors Hub entries in the
  // header's profile dropdown; decoded from the ID token we already hold in redux.
  const realmRoles = useMemo(() => getRealmRolesFromIdToken(auth?.idToken), [auth?.idToken]);
  // A user_hint cookie means this domain had a session on the previous visit, so paint the
  // avatar -- and the name when the hint carries one -- instead of the sign-in CTA while
  // the Keycloak restore and the profile fetch are still in flight. Read once on mount:
  // the Keycloak restore below is the source of truth and replaces it, and a logout
  // clears the cookie.
  const userHint = useMemo(() => readUserHint(), []);
  const navigate = useRouteNavigate();
  const location = useLocation();
  const [savedSearchesModalOpen, setSavedSearchesModalOpen] = useState(false);

  const pathLocale = locale == 'ar' ? '' : `/${locale}`;
  const isPostListingPage = location.pathname.includes('/post-listing');
  const onDeleteSavedSearch = (item) => {
    deleteSavedSearch({ locale: locale, id: item?.id, userID: userData?.external_id })
      .then(() => {
        setSavedSearches((prevSavedSearches) => prevSavedSearches.filter((search) => search.id !== item?.id));
      })
      .catch((error) => {
        console.error('Error deleting saved search:', error);
      });
  };
  const countryList = useMemo(
    () => [
      {
        title: 'United Arab Emirates',
        title_l1: 'الإمارات العربية المتحدة',
        href: 'https://www.bayut.com/',
        id: 'UAE',
        icon: 'AE',
      },
      { title: 'Saudi Arabia', title_l1: 'السعودية', href: 'https://www.bayut.sa/', icon: 'SA', id: 'SA' },
      {
        title: 'Pakistan',
        title_l1: 'باكستان',
        href: 'https://www.zameen.com/',
        id: 'PK',
        icon: 'PK',
      },
      {
        title: 'Oman',
        title_l1: 'الأردن',
        href: 'https://www.bayut.om/',
        id: 'OM',
        icon: 'OM',
      },
      {
        title: 'Qatar',
        title_l1: 'الأردن',
        href: 'https://www.bayut.qa/',
        id: 'QA',
        icon: 'QA',
      },
      {
        title: 'Bahrain',
        title_l1: 'الأردن',
        href: 'https://www.bayut.bh/',
        id: 'BH',
        icon: 'BH',
      },
      {
        title: 'Jordan',
        title_l1: 'الأردن',
        href: 'https://www.bayut.jo/',
        id: 'JO',
        icon: 'JO',
      },
      {
        title: 'Philippines',
        title_l1: 'الفلبين',
        href: 'https://www.lamudi.com.ph/',
        id: 'PH',
        icon: 'PH',
      },
      {
        title: 'Indonedia',
        title_l1: 'إندونيسيا',
        href: 'https://www.lamudi.co.id/',
        id: 'ID',
        icon: 'ID',
      },
      { title: 'Egypt', title_l1: 'مَصر', href: 'https://www.bayut.eg/', id: 'EG', icon: 'EG', size: '16px' },
    ],
    [],
  );

  const localeData = useMemo(
    () => [
      { label: 'English', value: 'en' },
      { label: newLocale === 'ar' ? <IconArabicBold size={52} /> : <IconArabicfont size={52} />, value: 'ar' },
    ],
    [newLocale],
  );

  const logoutUser = async () => {
    onLogout({ manualLogout: true });
  };

  const saveSettings = async (payload) => {
    setCookieUnits(payload);
    await saveSetting({ payload });
  };

  const toggleLanguage = (loc) => {
    const currentUrl = window?.location?.pathname + window?.location?.search;
    let newUrl;
    if (currentUrl.includes(`/${locale}/`)) {
      newUrl = currentUrl.replace(`/${locale}/`, `${loc == 'ar' ? '/ar' : `/${loc}`}/`);
    } else {
      newUrl = `/${loc}${window?.location?.pathname + window?.location?.search}`;
    }
    return newUrl;
  };

  const languageObjectToSwitch = useMemo(
    () => tenantConstants.LANGUAGES?.length > 1 && tenantConstants.LANGUAGES?.find((e) => e.alternate === locale),
    [locale],
  );

  const getNoFavouritePropertyModalUi = () => {
    if (hideFavouriteProperties) return null;

    return (
      <LiteNoDataModalUi
        visible={noFavPropModal}
        onCancel={() => setNoFavPropModal(false)}
        icon="IconFavouriteProperties"
        title={t("You haven't added any favourite properties yet")}
        subtitle={t('Simply tap on the HEART icon to bookmark your favourite properties and explore them later')}
      />
    );
  };

  useEffect(() => {
    if (!hideFavouriteProperties) {
      fetchFavProperties();
    }
    if (!hideSavedSearches) {
      fetchSavedSearch();
    }
  }, [auth?.authenticated, hideFavouriteProperties, hideSavedSearches]);

  useEffect(() => {
    if (!auth?.authenticated || !tenantConstants.MY_BOOKINGS_PATH) {
      setMyBookingsCount(0);
      return;
    }
    getBookings()
      .unwrap()
      .then((data) => {
        setMyBookingsCount(typeof data?.total === 'number' && !Number.isNaN(data.total) ? data.total : 0);
      })
      .catch(() => setMyBookingsCount(0));
  }, [auth?.authenticated]);

  const fetchFavProperties = async () => {
    if (auth?.authenticated) {
      setIsFavpropLoading(true);
      const response = await getFavouriteProperties();
      if (!!response.data) {
        setFavouriteProperties(
          response.data?.map((e) => ({
            value: e,
            label: getLink(e, `/property/details-${e}.html`, true, null, locale),
          })),
        );
      }
      setIsFavpropLoading(false);
    }
  };

  const fetchSavedSearch = async () => {
    if (auth?.authenticated) {
      const response = await getUsersSavedSearches({ locale });
      if (!!response?.data) {
        setSavedSearches(response?.data);
      }
    }
  };

  const getNoSavedSearchesModalUi = () => {
    if (hideSavedSearches) return null;

    return (
      <LiteNoDataModalUi
        visible={noSaveSearchModal}
        onCancel={() => setNoSaveSearchModal(false)}
        icon="IconSavedSearched"
        title={t("You haven't saved any searches yet")}
        subtitle={t('Use the SAVE SEARCH button below the filters to bookmark your property preferences')}
      />
    );
  };

  if (tenantConstants.BAYUT_KSA_HEADER_ENABLED) {
    // Arabic is the default locale and is never prefixed (/listings), only
    // English is prefixed (/en/listings). Strip any existing /en to get the
    // locale-agnostic path, then build each language's URL from it.
    const basePath = window.location.pathname.replace(/^\/en(?=\/|$)/, '') || '/';
    const search = window.location.search || '';
    const alternateURLs = {
      ar: `${basePath}${search}`,
      en: `/en${basePath === '/' ? '' : basePath}${search}`,
    };

    return (
      <>
        <SavedSearchesModal
          open={savedSearchesModalOpen}
          onClose={() => setSavedSearchesModalOpen(false)}
          savedSearches={savedSearches}
          onDelete={onDeleteSavedSearch}
          buildHref={(item) => `${getClassifiedBaseURL()}${locale === 'en' ? '/en' : ''}${item?.url}`}
        />
        <BayutKsaHeaderWrapper
          style={{
            backgroundColor: 'white',
            fontFamily: locale === 'ar' ? "'Droid Arabic Kufi', 'Lato', sans-serif" : "'Lato', sans-serif",
          }}
        >
          <Header
              language={locale}
              baseURL={getClassifiedBaseURL()}
              alternateURLs={alternateURLs}
              truEstimateEnabled
              // Show the geographical zones page link (prop added in @bayut-sa/header 1.0.31).
              geographicalZonesEnabled
              // Show the Find My Property page link (prop added in @bayut-sa/header 1.0.39).
              findMyPropertyEnabled
              appDownloadSection={
                <li style={{ padding: '8px 16px 12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '14px' }}>
                    <span className="fw-700 fz-18">{t('Download the Bayut app:')}</span>
                    <a
                      style={{ display: 'inline-block' }}
                      href={
                        isAndroid()
                          ? tenantConstants.APP_LOGO.linkPlayStore?.[locale]
                          : tenantConstants.APP_LOGO.linkIos?.[locale]
                      }
                    >
                      {isAndroid() ? (
                        locale === 'en' ? (
                          <PlayStoreBadgeIcon width="132" height="44" />
                        ) : (
                          <BayutPlayStoreArabicIcon width="132" height="44" />
                        )
                      ) : locale === 'en' ? (
                        <BayutAppStoreIcon width="132" height="44" />
                      ) : (
                        <BayutAppStoreArabicIcon width="132" height="44" />
                      )}
                    </a>
                  </div>
                </li>
              }
              onLogin={() => {
                window.location.href = getLoginPath();
              }}
              onPostAd={() => {
                navigate(tenantRoutes.app('', false, userData).post_listing.path);
                addPropertyEvent(userData, getCurrentLocationPath(location), false);
              }}
              user={
                auth?.authenticated && userData
                  ? {
                      name: tenantUtils.getLocalisedString(userData, 'name'),
                      image: userData?.image ?? undefined,
                      realmRoles,
                    }
                  : userHint
                    ? // Optimistic placeholder: an empty name still renders the avatar, so
                      // the header never flashes the sign-in CTA at a returning user.
                      { name: userHint.name || '' }
                    : null
              }
              favoritesCount={favouriteProperties?.length || 0}
              savedSearchesCount={savedSearches?.length || 0}
              myBookingsCount={myBookingsCount}
              onFavoritesClick={() => {
                // Lives on the classified site, so a full navigation rather than the
                // in-app router. Arabic is unprefixed, English is /en -- same rule the
                // saved-searches links above use.
                window.location.href = `${getClassifiedBaseURL()}${locale === 'en' ? '/en' : ''}/favorite-properties.html`;
              }}
              onSavedSearchesClick={() => setSavedSearchesModalOpen(true)}
              onAccountSettingsClick={() => {
                navigate(`${tenantRoutes.app('', false, userData).settings.route}/user-profile`);
              }}
              onLogout={logoutUser}
            />
        </BayutKsaHeaderWrapper>
      </>
    );
  }

  return (
    <>
      {getNoFavouritePropertyModalUi()}
      {getNoSavedSearchesModalUi()}
      {!hideFloatingPostAdButton && !isPostListingPage && (
        <Tooltip {...(isMobile && { open: false })} title={t('Post an Ad')} placement={rtl ? 'left' : 'right'}>
          <FAB
            type="success"
            icon="IconPostListing"
            shape="circle"
            size="large"
            onClick={() => {
              navigate(tenantRoutes.app('', false, userData).post_listing.path);
              addPropertyEvent(userData, getCurrentLocationPath(location), false);
            }}
          />
        </Tooltip>
      )}
      {isMobile ? (
        <TenantComponents.MobileNavBar
          isMobile={isMobile}
          userData={userData}
          logoutUser={logoutUser}
          localeData={localeData}
          locale={locale}
          onDeleteSavedSearch={onDeleteSavedSearch}
          countryList={countryList}
          setShowLocaleModal={setShowLocaleModal}
          setNoFavPropModal={setNoFavPropModal}
          isFavpropLoading={isFavpropLoading}
          setNoSaveSearchModal={setNoSaveSearchModal}
          saveSettings={saveSettings}
          pathLocale={pathLocale}
          toggleLanguage={toggleLanguage}
          languageObj={languageObjectToSwitch}
          savedSearches={savedSearches}
          setSavedSearches={setSavedSearches}
          favouriteProperties={favouriteProperties}
          fullWidth={fullWidth}
          hideMobileNavBarDrawer={hideMobileNavBarDrawer}
        />
      ) : (
        <TenantComponents.DeskTopNavBar
          userData={userData}
          logoutUser={logoutUser}
          setNewLocale={setNewLocale}
          locale={locale}
          onDeleteSavedSearch={onDeleteSavedSearch}
          setNoSaveSearchModal={setNoSaveSearchModal}
          savedSearches={savedSearches}
          setSavedSearches={setSavedSearches}
          favouriteProperties={favouriteProperties}
          setShowLocaleModal={setShowLocaleModal}
          showChangeCurrencyModal={showChangeCurrencyModal}
          setShowChangeCurrencyModal={setShowChangeCurrencyModal}
          showChangeAreaUnitModal={showChangeAreaUnitModal}
          setShowChangeAreaUnitModal={setShowChangeAreaUnitModal}
          setNoFavPropModal={setNoFavPropModal}
          isFavpropLoading={isFavpropLoading}
          saveSettings={saveSettings}
          pathLocale={pathLocale}
          toggleLanguage={toggleLanguage}
          languageObj={languageObjectToSwitch}
          isAuthenticated={auth?.authenticated}
          fullWidth={fullWidth}
          hideLoginButton={hideLoginButton}
          hideSavedSearches={hideSavedSearches}
          hideFavouriteProperties={hideFavouriteProperties}
          hideMyListings={hideMyListings}
          hidePostAd={hidePostAd}
          hideCurrencyConverter={hideCurrencyConverter}
        />
      )}
    </>
  );
};
